import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { IndianRupee, Wallet, Clock, Map, Navigation, Percent, Users, MessageSquare, CheckCircle, XCircle, AlertCircle, RefreshCw } from "lucide-react";

export const OTPConfigPage = () => {
  const [config, setConfig] = useState(null);
  const [routingConfig, setRoutingConfig] = useState(null);
  const [globalSettings, setGlobalSettings] = useState(null);
  const [otpSettings, setOtpSettings] = useState({ otp_provider: 'whatsapp' });
  const [otpStatus, setOtpStatus] = useState(null);
  const [checkingOtpStatus, setCheckingOtpStatus] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingRouting, setSavingRouting] = useState(false);
  const [savingGlobal, setSavingGlobal] = useState(false);
  const [savingOtp, setSavingOtp] = useState(false);

  useEffect(() => { 
    fetchConfig(); 
    fetchRoutingConfig();
    fetchGlobalSettings();
    fetchOtpSettings();
    fetchOtpStatus();
  }, []);

  const fetchOtpStatus = async () => {
    setCheckingOtpStatus(true);
    try {
      const res = await axios.get(`${API}/auth/otp/status`);
      setOtpStatus(res.data);
    } catch (err) {
      console.error("Failed to check OTP status:", err);
      setOtpStatus({ status: "error", message: err.response?.data?.detail || "Failed to check status" });
    } finally {
      setCheckingOtpStatus(false);
    }
  };

  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API}/superadmin/config`);
      setConfig(res.data);
    } catch (err) {
      toast.error("Failed to load configuration");
    } finally {
      setLoading(false);
    }
  };

  const fetchRoutingConfig = async () => {
    try {
      const res = await axios.get(`${API}/superadmin/routing-settings`);
      setRoutingConfig(res.data);
    } catch (err) {
      console.error("Failed to load routing config:", err);
    }
  };

  const fetchGlobalSettings = async () => {
    try {
      const res = await axios.get(`${API}/superadmin/global-settings`);
      setGlobalSettings(res.data);
    } catch (err) {
      console.error("Failed to load global settings:", err);
    }
  };

  const fetchOtpSettings = async () => {
    try {
      const res = await axios.get(`${API}/superadmin/otp-settings`);
      setOtpSettings(res.data);
    } catch (err) {
      console.error("Failed to load OTP settings:", err);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/superadmin/config`, {
        otp_price: config.otp_price,
        min_recharge: config.min_recharge,
        monthly_subscription: config.monthly_subscription,
        quarterly_subscription: config.quarterly_subscription,
        half_yearly_subscription: config.half_yearly_subscription,
        yearly_subscription: config.yearly_subscription,
        monthly_enabled: config.monthly_enabled,
        quarterly_enabled: config.quarterly_enabled,
        half_yearly_enabled: config.half_yearly_enabled,
        yearly_enabled: config.yearly_enabled,
        razorpay_key_id: config.razorpay_key_id,
        razorpay_key_secret: config.razorpay_key_secret
      });
      toast.success("Configuration saved successfully!");
      fetchConfig();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveRouting = async () => {
    setSavingRouting(true);
    try {
      await axios.put(`${API}/superadmin/routing-settings`, {
        routing_service: routingConfig.routing_service,
        openrouteservice_api_key: routingConfig.openrouteservice_api_key,
        google_maps_api_key: routingConfig.google_maps_api_key,
        use_google_maps: routingConfig.use_google_maps
      });
      toast.success("Routing settings saved successfully!");
      fetchRoutingConfig();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save routing settings");
    } finally {
      setSavingRouting(false);
    }
  };

  const handleSaveGlobal = async () => {
    setSavingGlobal(true);
    try {
      await axios.put(`${API}/superadmin/global-settings`, globalSettings);
      toast.success("Global commission settings saved!");
      fetchGlobalSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save global settings");
    } finally {
      setSavingGlobal(false);
    }
  };

  const handleSaveOtpSettings = async () => {
    setSavingOtp(true);
    try {
      await axios.put(`${API}/superadmin/otp-settings`, {
        otp_provider: otpSettings.otp_provider
      });
      toast.success(`OTP delivery set to ${otpSettings.otp_provider === 'whatsapp' ? 'WhatsApp' : 'SMS'}!`);
      fetchOtpSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save OTP settings");
    } finally {
      setSavingOtp(false);
    }
  };

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div data-testid="otp-config-page">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground">Settings</h2>
        <p className="text-muted-foreground">Manage platform-wide pricing and configuration</p>
      </div>

      <div className="max-w-2xl space-y-6">
        {/* OTP Pricing */}
        <div className="bg-card rounded-xl border border-border p-4 md:p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
              <IndianRupee className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">OTP Pricing</h3>
              <p className="text-sm text-muted-foreground">Price charged per OTP sent</p>
            </div>
          </div>
          <div>
            <Label>Price per OTP (₹)</Label>
            <Input type="number" min={0} step={0.01} value={config?.otp_price || 0.50} onChange={(e) => setConfig({ ...config, otp_price: parseFloat(e.target.value) })} className="mt-1 max-w-xs" />
            <p className="text-xs text-muted-foreground mt-1">Deducted from admin wallet for each OTP (after trial)</p>
          </div>
        </div>

        {/* OTP Service Status - Health Check */}
        <div className="bg-card rounded-xl border border-border p-4 md:p-6" data-testid="otp-status-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                otpStatus?.status === 'ready' ? 'bg-green-100' : 
                otpStatus?.status === 'error' ? 'bg-red-100' : 'bg-yellow-100'
              }`}>
                {otpStatus?.status === 'ready' ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : otpStatus?.status === 'error' ? (
                  <XCircle className="w-5 h-5 text-red-600" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-yellow-600" />
                )}
              </div>
              <div>
                <h3 className="font-semibold text-foreground">OTP Service Status</h3>
                <p className="text-sm text-muted-foreground">Check if OTP services are properly configured</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={fetchOtpStatus}
              disabled={checkingOtpStatus}
              data-testid="refresh-otp-status-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${checkingOtpStatus ? 'animate-spin' : ''}`} />
              {checkingOtpStatus ? 'Checking...' : 'Refresh'}
            </Button>
          </div>

          {otpStatus && (
            <div className="space-y-3">
              {/* Overall Status */}
              <div className={`p-3 rounded-lg ${
                otpStatus.status === 'ready' ? 'bg-green-50 border border-green-200' :
                otpStatus.status === 'error' ? 'bg-red-50 border border-red-200' :
                'bg-yellow-50 border border-yellow-200'
              }`}>
                <p className={`font-medium ${
                  otpStatus.status === 'ready' ? 'text-green-700' :
                  otpStatus.status === 'error' ? 'text-red-700' :
                  'text-yellow-700'
                }`}>
                  {otpStatus.status === 'ready' ? '✓ OTP Service is Ready' :
                   otpStatus.status === 'error' ? '✗ OTP Service Error' :
                   '⚠ OTP Service Not Configured'}
                </p>
                {otpStatus.message && (
                  <p className="text-sm mt-1 opacity-80">{otpStatus.message}</p>
                )}
              </div>

              {/* Configuration Details */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-muted-foreground">SMS Enabled</p>
                  <p className={`font-medium ${otpStatus.sms_enabled ? 'text-green-600' : 'text-red-600'}`}>
                    {otpStatus.sms_enabled ? 'Yes' : 'No'}
                  </p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-muted-foreground">Provider</p>
                  <p className="font-medium capitalize">{otpStatus.otp_provider || 'Unknown'}</p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-muted-foreground">WhatsApp</p>
                  <p className={`font-medium ${otpStatus.whatsapp_configured ? 'text-green-600' : 'text-red-600'}`}>
                    {otpStatus.whatsapp_configured ? 'Configured' : 'Not Configured'}
                  </p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-muted-foreground">SMS</p>
                  <p className={`font-medium ${otpStatus.sms_configured ? 'text-green-600' : 'text-red-600'}`}>
                    {otpStatus.sms_configured ? 'Configured' : 'Not Configured'}
                  </p>
                </div>
              </div>

              {/* Sender Info */}
              {(otpStatus.whatsapp_sender || otpStatus.sms_sender_id) && (
                <div className="text-xs text-muted-foreground mt-2">
                  {otpStatus.whatsapp_sender && <p>WhatsApp Sender: {otpStatus.whatsapp_sender}</p>}
                  {otpStatus.sms_sender_id && <p>SMS Sender ID: {otpStatus.sms_sender_id}</p>}
                </div>
              )}
            </div>
          )}

          {!otpStatus && !checkingOtpStatus && (
            <p className="text-muted-foreground text-sm">Click Refresh to check OTP service status</p>
          )}
        </div>

        {/* OTP Delivery Method */}
        <div className="bg-card rounded-xl border border-border p-4 md:p-6" data-testid="otp-delivery-settings">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">OTP Delivery Method</h3>
              <p className="text-sm text-muted-foreground">Choose how OTP is sent to users</p>
            </div>
          </div>

          <div className="grid gap-3">
            <div 
              className={`flex items-center justify-between p-4 rounded-lg border-2 cursor-pointer transition-all ${
                otpSettings?.otp_provider === 'whatsapp' 
                  ? 'border-green-500 bg-green-50' 
                  : 'border-border hover:border-green-300'
              }`}
              onClick={() => setOtpSettings({ ...otpSettings, otp_provider: 'whatsapp' })}
              data-testid="otp-whatsapp-option"
            >
              <div className="flex items-center gap-3">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  otpSettings?.otp_provider === 'whatsapp' ? 'border-green-500' : 'border-muted-foreground'
                }`}>
                  {otpSettings?.otp_provider === 'whatsapp' && <div className="w-2.5 h-2.5 rounded-full bg-green-500" />}
                </div>
                <div>
                  <p className="font-medium flex items-center gap-2">
                    WhatsApp
                    <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full">Recommended</span>
                  </p>
                  <p className="text-sm text-muted-foreground">Send OTP via WhatsApp message (higher delivery rate)</p>
                </div>
              </div>
              <svg className="w-6 h-6 text-green-600" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
            </div>

            <div 
              className={`flex items-center justify-between p-4 rounded-lg border-2 cursor-pointer transition-all ${
                otpSettings?.otp_provider === 'sms' 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-border hover:border-blue-300'
              }`}
              onClick={() => setOtpSettings({ ...otpSettings, otp_provider: 'sms' })}
              data-testid="otp-sms-option"
            >
              <div className="flex items-center gap-3">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  otpSettings?.otp_provider === 'sms' ? 'border-blue-500' : 'border-muted-foreground'
                }`}>
                  {otpSettings?.otp_provider === 'sms' && <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />}
                </div>
                <div>
                  <p className="font-medium">SMS</p>
                  <p className="text-sm text-muted-foreground">Send OTP via traditional SMS message</p>
                </div>
              </div>
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
            </div>
          </div>

          <p className="text-xs text-muted-foreground mt-4">
            WhatsApp delivery has higher success rates and is preferred. SMS is used as fallback if WhatsApp fails.
          </p>

          <div className="flex justify-end mt-4">
            <Button onClick={handleSaveOtpSettings} disabled={savingOtp} className="min-w-32" data-testid="save-otp-settings-btn">
              {savingOtp ? "Saving..." : "Save OTP Settings"}
            </Button>
          </div>
        </div>

        {/* Wallet Settings */}
        <div className="bg-card rounded-xl border border-border p-4 md:p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
              <Wallet className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">Wallet Settings</h3>
              <p className="text-sm text-muted-foreground">Configure wallet recharge options</p>
            </div>
          </div>
          <div>
            <Label>Minimum Recharge (₹)</Label>
            <Input type="number" min={100} step={100} value={config?.min_recharge || 1000} onChange={(e) => setConfig({ ...config, min_recharge: parseFloat(e.target.value) })} className="mt-1 max-w-xs" />
          </div>
        </div>

        {/* Subscription Plans */}
        <div className="bg-card rounded-xl border border-border p-4 md:p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <Clock className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">Subscription Plans</h3>
              <p className="text-sm text-muted-foreground">Configure subscription pricing</p>
            </div>
          </div>

          <div className="space-y-3">
            {/* Monthly */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-3">
                <Switch checked={config?.monthly_enabled ?? true} onCheckedChange={(checked) => setConfig({ ...config, monthly_enabled: checked })} />
                <Label className="font-medium">Monthly</Label>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">₹</span>
                <Input type="number" min={0} value={config?.monthly_subscription || 999} onChange={(e) => setConfig({ ...config, monthly_subscription: parseFloat(e.target.value) })} className="w-24" />
              </div>
            </div>

            {/* Quarterly */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-3">
                <Switch checked={config?.quarterly_enabled ?? false} onCheckedChange={(checked) => setConfig({ ...config, quarterly_enabled: checked })} />
                <Label className="font-medium">Quarterly (3 months)</Label>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">₹</span>
                <Input type="number" min={0} value={config?.quarterly_subscription || 2499} onChange={(e) => setConfig({ ...config, quarterly_subscription: parseFloat(e.target.value) })} className="w-24" />
              </div>
            </div>

            {/* Half Yearly */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-3">
                <Switch checked={config?.half_yearly_enabled ?? false} onCheckedChange={(checked) => setConfig({ ...config, half_yearly_enabled: checked })} />
                <Label className="font-medium">Half Yearly (6 months)</Label>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">₹</span>
                <Input type="number" min={0} value={config?.half_yearly_subscription || 4499} onChange={(e) => setConfig({ ...config, half_yearly_subscription: parseFloat(e.target.value) })} className="w-24" />
              </div>
            </div>

            {/* Yearly */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-3">
                <Switch checked={config?.yearly_enabled ?? true} onCheckedChange={(checked) => setConfig({ ...config, yearly_enabled: checked })} />
                <Label className="font-medium">Yearly</Label>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">₹</span>
                <Input type="number" min={0} value={config?.yearly_subscription || 7999} onChange={(e) => setConfig({ ...config, yearly_subscription: parseFloat(e.target.value) })} className="w-24" />
              </div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-3">Toggle to enable/disable plans for admin subscriptions.</p>
        </div>

        {/* Razorpay Configuration */}
        <div className="bg-card rounded-xl border border-border p-4 md:p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
              <IndianRupee className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">Razorpay Configuration</h3>
              <p className="text-sm text-muted-foreground">Payment gateway for wallet recharge</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Razorpay Key ID</Label>
              <Input type="text" value={config?.razorpay_key_id || ''} onChange={(e) => setConfig({ ...config, razorpay_key_id: e.target.value })} placeholder="rzp_live_xxxxxxxxxx" className="mt-1" />
            </div>
            <div>
              <Label>Razorpay Key Secret</Label>
              <Input type="password" value={config?.razorpay_key_secret || ''} onChange={(e) => setConfig({ ...config, razorpay_key_secret: e.target.value })} placeholder="Enter secret key" className="mt-1" />
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Get your Razorpay keys from <a href="https://dashboard.razorpay.com" target="_blank" rel="noopener noreferrer" className="text-primary underline">dashboard.razorpay.com</a>
          </p>
        </div>

        {/* Admin Approval Settings */}
        <div className="bg-card rounded-xl border border-border p-4 md:p-6" data-testid="admin-approval-settings">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">Admin Registration Settings</h3>
              <p className="text-sm text-muted-foreground">Control how new admins are onboarded</p>
            </div>
          </div>

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 bg-muted/50 rounded-lg">
            <div>
              <Label className="font-medium">Auto-Approve New Admins</Label>
              <p className="text-sm text-muted-foreground mt-1">
                When enabled, new admins can login immediately after registration.
                When disabled, Super Admin must manually approve each new admin.
              </p>
            </div>
            <Switch
              checked={globalSettings?.admin_auto_approve !== false}
              onCheckedChange={(checked) => setGlobalSettings({ ...globalSettings, admin_auto_approve: checked })}
            />
          </div>
          
          <div className="flex justify-end mt-4">
            <Button onClick={handleSaveGlobal} disabled={savingGlobal} variant="outline" className="min-w-32">
              {savingGlobal ? "Saving..." : "Save Admin Settings"}
            </Button>
          </div>
        </div>

        {/* Save General Config Button */}
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving} className="min-w-32">
            {saving ? "Saving..." : "Save Configuration"}
          </Button>
        </div>

        {/* Global Commission Settings */}
        <div className="bg-card rounded-xl border border-border p-4 md:p-6" data-testid="global-commission-settings">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
              <Percent className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">Global Commission Settings</h3>
              <p className="text-sm text-muted-foreground">Default commission rates for all admins</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label>Default Commission Type</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => setGlobalSettings({ ...globalSettings, default_commission_type: 'percentage' })}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                    globalSettings?.default_commission_type === 'percentage'
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <p className="font-medium">Percentage</p>
                  <p className="text-xs text-muted-foreground">% of each ride fare</p>
                </button>
                <button
                  type="button"
                  onClick={() => setGlobalSettings({ ...globalSettings, default_commission_type: 'flat_per_ride' })}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                    globalSettings?.default_commission_type === 'flat_per_ride'
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <p className="font-medium">Flat Per Ride</p>
                  <p className="text-xs text-muted-foreground">Fixed amount per ride</p>
                </button>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Default Percentage Rate (%)</Label>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  step={0.5}
                  value={globalSettings?.default_commission_rate || 10}
                  onChange={(e) => setGlobalSettings({ ...globalSettings, default_commission_rate: parseFloat(e.target.value) })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Default Flat Rate (₹)</Label>
                <Input
                  type="number"
                  min={0}
                  step={1}
                  value={globalSettings?.default_flat_per_ride || 10}
                  onChange={(e) => setGlobalSettings({ ...globalSettings, default_flat_per_ride: parseFloat(e.target.value) })}
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label>Default Driver Owed Limit (₹)</Label>
              <Input
                type="number"
                min={0}
                step={100}
                value={globalSettings?.default_driver_owed_limit || 500}
                onChange={(e) => setGlobalSettings({ ...globalSettings, default_driver_owed_limit: parseFloat(e.target.value) })}
                className="mt-1 max-w-xs"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Maximum amount drivers can owe before being blocked from rides
              </p>
            </div>
          </div>

          <div className="flex justify-end mt-4">
            <Button onClick={handleSaveGlobal} disabled={savingGlobal} variant="outline" className="min-w-32">
              {savingGlobal ? "Saving..." : "Save Commission Settings"}
            </Button>
          </div>
        </div>

        {/* Distance Calculation / Routing Settings */}
        <div className="bg-card rounded-xl border border-border p-4 md:p-6" data-testid="routing-settings-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
              <Navigation className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">Distance Calculation</h3>
              <p className="text-sm text-muted-foreground">Configure how ride distances are calculated for fares</p>
            </div>
          </div>

          {/* Routing Service Selection */}
          <div className="mb-6">
            <Label className="mb-2 block">Distance Calculation Method</Label>
            <div className="grid gap-3">
              <div 
                className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                  routingConfig?.routing_service === 'aerial' 
                    ? 'border-primary bg-primary/5' 
                    : 'border-border hover:border-muted-foreground'
                }`}
                onClick={() => setRoutingConfig({ ...routingConfig, routing_service: 'aerial', use_google_maps: false })}
                data-testid="routing-aerial-option"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                    routingConfig?.routing_service === 'aerial' ? 'border-primary' : 'border-muted-foreground'
                  }`}>
                    {routingConfig?.routing_service === 'aerial' && <div className="w-2 h-2 rounded-full bg-primary" />}
                  </div>
                  <div>
                    <p className="font-medium">Aerial (Straight-line)</p>
                    <p className="text-xs text-muted-foreground">Free - Uses straight-line distance with 1.3x multiplier</p>
                  </div>
                </div>
                <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded">Free</span>
              </div>

              <div 
                className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                  routingConfig?.routing_service === 'openrouteservice' 
                    ? 'border-primary bg-primary/5' 
                    : 'border-border hover:border-muted-foreground'
                }`}
                onClick={() => setRoutingConfig({ ...routingConfig, routing_service: 'openrouteservice', use_google_maps: false })}
                data-testid="routing-ors-option"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                    routingConfig?.routing_service === 'openrouteservice' ? 'border-primary' : 'border-muted-foreground'
                  }`}>
                    {routingConfig?.routing_service === 'openrouteservice' && <div className="w-2 h-2 rounded-full bg-primary" />}
                  </div>
                  <div>
                    <p className="font-medium">OpenRouteService</p>
                    <p className="text-xs text-muted-foreground">Actual road distance via free/paid API</p>
                  </div>
                </div>
                <span className={`text-xs px-2 py-1 rounded ${
                  routingConfig?.openrouteservice_configured 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-amber-100 text-amber-700'
                }`}>
                  {routingConfig?.openrouteservice_configured ? 'Configured' : 'Key Required'}
                </span>
              </div>

              <div 
                className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                  routingConfig?.routing_service === 'google_maps' 
                    ? 'border-primary bg-primary/5' 
                    : 'border-border hover:border-muted-foreground'
                }`}
                onClick={() => setRoutingConfig({ ...routingConfig, routing_service: 'google_maps', use_google_maps: true })}
                data-testid="routing-google-option"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                    routingConfig?.routing_service === 'google_maps' ? 'border-primary' : 'border-muted-foreground'
                  }`}>
                    {routingConfig?.routing_service === 'google_maps' && <div className="w-2 h-2 rounded-full bg-primary" />}
                  </div>
                  <div>
                    <p className="font-medium">Google Maps Directions API</p>
                    <p className="text-xs text-muted-foreground">Most accurate road distance (paid service)</p>
                  </div>
                </div>
                <span className={`text-xs px-2 py-1 rounded ${
                  routingConfig?.google_maps_configured 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-amber-100 text-amber-700'
                }`}>
                  {routingConfig?.google_maps_configured ? 'Configured' : 'Key Required'}
                </span>
              </div>
            </div>
          </div>

          {/* API Keys */}
          <div className="space-y-4 pt-4 border-t border-border">
            <h4 className="font-medium text-sm text-muted-foreground">API Keys (Optional - Leave blank to use Aerial)</h4>
            
            <div>
              <Label>OpenRouteService API Key</Label>
              <Input 
                type="password" 
                value={routingConfig?.openrouteservice_api_key || ''} 
                onChange={(e) => setRoutingConfig({ ...routingConfig, openrouteservice_api_key: e.target.value })} 
                placeholder={routingConfig?.openrouteservice_configured ? "****" : "Enter OpenRouteService API key"}
                className="mt-1"
                data-testid="ors-api-key-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Get free key from <a href="https://openrouteservice.org/dev/#/signup" target="_blank" rel="noopener noreferrer" className="text-primary underline">openrouteservice.org</a> (2,000 requests/day free)
              </p>
            </div>

            <div>
              <Label>Google Maps API Key</Label>
              <Input 
                type="password" 
                value={routingConfig?.google_maps_api_key || ''} 
                onChange={(e) => setRoutingConfig({ ...routingConfig, google_maps_api_key: e.target.value })} 
                placeholder={routingConfig?.google_maps_configured ? "****" : "Enter Google Maps API key"}
                className="mt-1"
                data-testid="google-api-key-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Get from <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="text-primary underline">Google Cloud Console</a> (enable Directions API)
              </p>
            </div>
          </div>

          <div className="flex justify-end mt-4">
            <Button onClick={handleSaveRouting} disabled={savingRouting} variant="outline" className="min-w-32" data-testid="save-routing-btn">
              {savingRouting ? "Saving..." : "Save Routing Settings"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OTPConfigPage;
